import {
    createContext,
    useCallback,
    useContext,
    type MouseEvent,
    type MouseEventHandler,
    type ReactNode,
} from 'react';
import { useSetAtom } from 'jotai';
import type { TileableModel } from '@rocco/ui/tile-shared/view-models/tile';

import { BulkEnquireSelectControl } from './BulkEnquireSelectControl';
import {
    bulkEnquirySelectionAtom,
    toggleBulkEnquiryItem,
    useBulkEnquiryModeActive,
    type BulkEnquiryItem,
} from './bulk-enquiry-state';
import { bulkEnquiryItemForModel } from './tile-enquiry-item';

/**
 * Enables the bulk-enquiry tile affordance. The directory tile wrappers are
 * shared with the legacy category pages, so {@link TileEnquiryOverlay} only
 * swaps in the select checkbox where this provider is present — the 5.0 browse
 * pages ({@link DirectoryBrowsePage}). Default off everywhere else.
 */
const BulkEnquiryEnabledContext = createContext(false);

export const BulkEnquiryEnabledProvider = BulkEnquiryEnabledContext.Provider;

/**
 * A directory tile's top-corner overlay: the Save heart normally, or — while
 * bulk-enquiry mode is active on a browse page — an "add to enquiry" checkbox in
 * its place (the two never show together, so they don't fight for the corner).
 * Falls back to the heart outside a browse page, when mode is off, or for a
 * non-enquirable model (e.g. an article).
 */
export const TileEnquiryOverlay = ({
    model,
    follow,
}: {
    model: TileableModel;
    follow: ReactNode;
}) => {
    const item = useBulkEnquiryItem(model);

    if (item) return <BulkEnquireSelectControl item={item} />;

    return <>{follow}</>;
};

/**
 * The selectable item this tile contributes while bulk-enquiry mode has taken
 * it over, or null when the tile keeps its normal behaviour.
 */
const useBulkEnquiryItem = (model: TileableModel): BulkEnquiryItem | null => {
    const enabled = useContext(BulkEnquiryEnabledContext);
    const modeActive = useBulkEnquiryModeActive();

    return enabled && modeActive ? bulkEnquiryItemForModel(model) : null;
};

/**
 * True while bulk-enquiry mode owns this tile: the corner shows the select
 * checkbox and the whole tile toggles selection.
 *
 * Surfaces rendered *outside* the tile link — the mobile action slot — have to
 * ask, because the overlay swap alone does not reach them. Left mounted, the
 * `•••` fights the select UX and its taps are eaten by the wrapper's
 * capture-phase handler anyway, toggling selection instead of opening.
 */
export const useBulkEnquirySelectActive = (model: TileableModel): boolean =>
    useBulkEnquiryItem(model) !== null;

/**
 * While bulk-enquiry mode is active on a browse page, the whole tile is the
 * select target — not just the corner checkbox. Returns a capture-phase click
 * handler for the tile's wrapper that swallows the click (so the tile link
 * doesn't navigate) and toggles the item; undefined when the mode is off or
 * the model isn't enquirable, leaving the tile's normal behaviour untouched.
 * Modified clicks (new tab, etc.) keep their native link behaviour. Selection
 * access is write-only so grid tiles don't re-render on every toggle.
 */
export const useTileEnquiryClickCapture = (
    model: TileableModel,
): MouseEventHandler | undefined => {
    const enabled = useContext(BulkEnquiryEnabledContext);
    const modeActive = useBulkEnquiryModeActive();
    const setItems = useSetAtom(bulkEnquirySelectionAtom);

    const capture = useCallback(
        (event: MouseEvent) => {
            if (
                event.defaultPrevented ||
                event.button !== 0 ||
                event.metaKey ||
                event.ctrlKey ||
                event.shiftKey ||
                event.altKey
            ) {
                return;
            }
            const item = bulkEnquiryItemForModel(model);
            if (!item) return;
            event.preventDefault();
            event.stopPropagation();
            setItems((prev) => toggleBulkEnquiryItem(prev, item));
        },
        [model, setItems],
    );

    return enabled && modeActive ? capture : undefined;
};
